import { cn } from "@/lib/utils";

export type StatCardTone = "default" | "success" | "warning" | "danger";

/**
 * Shown when a caller supplies no value. Callers normally pass the dash
 * themselves (`formatUsdValue` returns UNKNOWN_VALUE_LABEL for a null value),
 * but a stat tile must never render as an empty box: an empty string here
 * reads as "the number is nothing" rather than "we do not have it".
 */
const UNKNOWN_VALUE = "—";

const VALUE_TONE: Record<StatCardTone, string> = {
  default: "text-ink",
  success: "text-success",
  warning: "text-warning",
  danger: "text-danger",
};

export type StatCardProps = {
  value: string;
  label: string;
  hint?: string | null;
  positiveHint?: boolean;
  tone?: StatCardTone;
};

export function StatCard({
  value,
  label,
  hint,
  positiveHint,
  tone = "default",
}: StatCardProps) {
  const displayValue = value.trim() === "" ? UNKNOWN_VALUE : value;

  return (
    <div className="rounded-lg border border-line bg-surface px-5 py-[18px]">
      <div className={cn("text-[28px] font-bold leading-[1.1] tracking-tight", VALUE_TONE[tone])}>{displayValue}</div>
      <div className="mt-1 text-xs text-muted">{label}</div>
      {hint && (
        <div className={`mt-0.5 text-xs ${positiveHint ? "text-success" : "text-muted"}`}>{hint}</div>
      )}
    </div>
  );
}

/**
 * The canonical stat row that sits above a table. Every page uses this so the
 * cards keep one grid, one gap and one breakpoint — the drift came from each
 * page re-declaring the wrapper classes.
 *
 * Renders nothing extra when `stats` is empty: callers decide whether to show
 * the row at all, so this stays a layout wrapper and never invents a card.
 */
export function StatCardRow({ stats, className }: { stats: StatCardProps[]; className?: string }) {
  return (
    <div className={cn("mb-5 grid grid-cols-2 gap-3 lg:grid-cols-4", className)}>
      {stats.map((s) => (
        <StatCard key={s.label} {...s} />
      ))}
    </div>
  );
}